import * as React from "react";
import { useState } from "react";
import {
    vorgangService,
    type VorgangPickerItem,
    type VorgangPickerTodo,
} from "../../../services/vorgangService";
import VorgangPickerAuswahl from "../picker/VorgangPickerAuswahl";
import { IcoX } from "../v3/vg3Icons";
// `VorgangPickerAuswahl` holt seine Optik aus `.cal-vgd-*` in calendar.css
// und importiert sie NICHT selbst — ohne diese Zeile steht die Liste nackt da.
import "../../calendar/calendar.css";
import "../v3/vorgangV3.css";

type Vorgang = VorgangPickerItem["vorgang"];

/**
 * „To-do hierher verschieben…" (PO 21.09.2026).
 *
 * ══ WOZU ═══════════════════════════════════════════════════════════════════
 * > „das ich ein todo von einem vorgang reinziehen kann verschieben kann."
 *
 * Ein To-do steht im falschen Vorgang — weil es beim Anlegen dort gelandet
 * ist, oder weil sich die Zuordnung als falsch herausgestellt hat. Bisher
 * blieb nur: drüben entfernen und hier neu tippen, und der ganze
 * Schriftwechsel bleibt drüben hängen.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ VERSCHIEBEN IST NICHT VERKNÜPFEN ═══════════════════════════════════════
 * Daneben gibt es `TodoVerknuepfenDialog` (v3, PO 05.09.2026), der fast gleich
 * aussieht und etwas anderes tut: Dort bleibt das To-do in seinem Vorgang zu
 * Hause und wird hier ZUSÄTZLICH angezeigt (`VorgangTodoLink`). Hier wechselt
 * es die Heimat — `Todo.vorgangId` selbst. Der Hinweistext sagt das
 * ausdrücklich, weil man die beiden Wege sonst verwechselt.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Alles, was am To-do hängt — Mails, Dokumente, Anrufe, Termine, Notizen —
 * wandert MIT, ohne dass hier etwas dafür zu tun wäre: diese Zeilen hängen an
 * der `todoId`, nicht am Vorgang. Die Bezüge des To-dos (Kunde, Risiko,
 * Vertrag) bleiben ebenfalls, wie sie sind; sie gehören dem To-do, nicht
 * seinem Elternvorgang.
 *
 * Zwei Schritte wie beim Verknüpfen: aus WELCHEM Vorgang, und dann WELCHES
 * To-do. Beides über `GET /vorgang/picker` und `/vorgang/:id/picker-todos` —
 * der Picker zeigt nur, was der Nutzer ohnehin sehen darf.
 */
type Props = {
    /** Der Vorgang, der das To-do aufnimmt — das ZIEL. */
    vorgangId: number;
    contactId?: number | null;
    onClose: () => void;
    /** Nach erfolgreichem Verschieben: die Seite neu laden. */
    onVerschoben: () => void | Promise<void>;
};

export const TodoVerschiebenDialog: React.FC<Props> = ({
    vorgangId,
    contactId = null,
    onClose,
    onVerschoben,
}) => {
    const [quelle, setQuelle] = useState<Vorgang | null>(null);
    const [todos, setTodos] = useState<VorgangPickerTodo[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const waehleVorgang = async (v: Vorgang) => {
        setQuelle(v);
        setTodos(null);
        setFehler(null);
        try {
            setTodos(await vorgangService.pickerTodos(v.id));
        } catch (err: any) {
            console.error("[TodoVerschieben] To-dos laden fehlgeschlagen", err);
            setFehler(err?.message ?? "To-dos konnten nicht geladen werden.");
            setTodos([]);
        }
    };

    const verschiebe = async (todo: VorgangPickerTodo) => {
        if (busy) return;
        setBusy(true);
        setFehler(null);
        try {
            await vorgangService.verschiebeTodo(todo.id, vorgangId);
            await onVerschoben();
            onClose();
        } catch (err: any) {
            console.error("[TodoVerschieben] Verschieben fehlgeschlagen", err);
            // Der Server lehnt zwei Fälle bewusst ab: fehlendes Schreibrecht am
            // To-do (403) und ein persönlicher Zielvorgang, in dem der
            // Bearbeiter nicht beteiligt ist (400). Beides steht in `message`.
            setFehler(err?.message ?? "Verschieben fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd vg3-modal-hg" role="presentation" onClick={onClose}>
            <div
                className="vg3-modal"
                role="dialog"
                aria-modal="true"
                aria-label="To-do aus einem anderen Vorgang hierher verschieben"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">To-do hierher verschieben</span>
                    <button
                        type="button"
                        className="vg3-ibtn"
                        title="Schließen"
                        aria-label="Schließen"
                        onClick={onClose}
                    >
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                <div className="vg3-modal-b">
                    {quelle == null ? (
                        <>
                            <p className="vg3-modal-hinweis">
                                Aus welchem Vorgang? Das To-do wechselt hierher — mit allem, was daran
                                hängt. In seinem bisherigen Vorgang steht es danach nicht mehr.
                            </p>
                            <VorgangPickerAuswahl
                                onPick={(v) => void waehleVorgang(v)}
                                contactId={contactId}
                                aktuelleVorgangId={vorgangId}
                                disabled={busy}
                            />
                        </>
                    ) : (
                        <>
                            <div className="vg3-modal-quelle">
                                <span className="vg3-modal-quelle-t">
                                    {quelle.vorgangNbr ? `${quelle.vorgangNbr} · ` : ""}
                                    {quelle.titel}
                                </span>
                                <button
                                    type="button"
                                    className="vg3-tbtn"
                                    disabled={busy}
                                    onClick={() => setQuelle(null)}
                                >
                                    anderer Vorgang
                                </button>
                            </div>

                            {todos == null ? (
                                <p className="vg3-modal-hinweis">To-dos werden geladen…</p>
                            ) : todos.length === 0 ? (
                                <p className="vg3-modal-hinweis">Dieser Vorgang hat keine To-dos.</p>
                            ) : (
                                <div className="vg3-modal-liste">
                                    {todos.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            className="vg3-modal-zeile"
                                            disabled={busy}
                                            onClick={() => void verschiebe(t)}
                                        >
                                            <span className="vg3-modal-zeile-t">
                                                {t.titel || "(ohne Betreff)"}
                                            </span>
                                            <span className="vg3-badge">{t.status}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TodoVerschiebenDialog;
